import { ICatalogue } from "../../apis/useGetCatalogueData/types";

/**
 * Per FOOD-2479 spec:
 * - Variant label is auto-composed from the cart line's customization options
 *   followed by its set items, joined by a middle-dot separator.
 * - At most MAX_OPTIONS entries are shown; any overflow is summarised as
 *   `… +N more` (en) / `… 還有 N 項` (cn).
 */
const MAX_OPTIONS = 3;
const SEPARATOR = " · ";

export interface IVirtualItemLabel {
	en: string;
	cn: string;
}

export interface IVirtualCartItem {
	line: ICatalogue;
	index: number;
	label: IVirtualItemLabel;
}

/**
 * A product is treated as customisable when it has at least one customization
 * with selectable options, or at least one set item. Only these products
 * participate in the big-card / virtual-item rendering branch.
 */
export const isCustomizableOrSet = (product: ICatalogue): boolean => {
	const hasCustomization =
		product.food_supplier_customizations?.some(
			(c) => (c.food_supplier_customization_options?.length ?? 0) > 0
		) ?? false;
	const hasSet = (product.food_supplier_set_items?.length ?? 0) > 0;

	return hasCustomization || hasSet;
};

interface IRawOption {
	en: string;
	cn: string;
}

const collectOptions = (line: ICatalogue): IRawOption[] => {
	const opts: IRawOption[] = [];

	for (const c of line.food_supplier_customizations ?? []) {
		const cTitle = c.title ?? "";
		const cTitleCn = c.title_cn ?? "";

		for (const o of c.food_supplier_customization_options ?? []) {
			const oTitle = o.title ?? "";
			const oTitleCn = o.title_cn ?? "";

			opts.push({
				en: cTitle ? `${cTitle} - ${oTitle}` : oTitle,
				cn: cTitleCn ? `${cTitleCn} - ${oTitleCn}` : oTitleCn,
			});
		}
	}

	for (const s of line.food_supplier_set_items ?? []) {
		opts.push({ en: s.title ?? "", cn: s.title_cn ?? "" });
	}

	return opts;
};

export const formatVirtualItemLabel = (line: ICatalogue): IVirtualItemLabel => {
	const opts = collectOptions(line);

	// Fallback when a cart line declares no customization options or set items
	// (e.g. the item carries a single optional customization that the user left
	// empty). Without this the variant row's label would render as an empty
	// string and the row would look broken.
	if (opts.length === 0) {
		return { en: "Original", cn: "原味" };
	}
	const head = opts.slice(0, MAX_OPTIONS);
	const overflow = opts.length - MAX_OPTIONS;
	const en = head.map((o) => o.en).join(SEPARATOR);
	const cn = head.map((o) => o.cn).join(SEPARATOR);

	if (overflow > 0) {
		return {
			en: `${en} … +${overflow} more`,
			cn: `${cn} … 還有 ${overflow} 項`,
		};
	}

	return { en, cn };
};

/**
 * Returns one virtual cart item per cart line whose brand_menu_item_id matches
 * the given product, preserving cart-source order so each virtual item carries
 * the cart index needed by UPDATE_CART_ITEM / REMOVE_CART and by detail-page
 * EDIT mode navigation.
 */
export const getVirtualItemsForProduct = (
	product: ICatalogue,
	cart: ICatalogue[] | undefined | null
): IVirtualCartItem[] => {
	if (!cart || cart.length === 0) {
		return [];
	}
	const result: IVirtualCartItem[] = [];

	cart.forEach((line, index) => {
		if (line.brand_menu_item_id === product.brand_menu_item_id) {
			result.push({ line, index, label: formatVirtualItemLabel(line) });
		}
	});

	return result;
};

/**
 * Locate the cart index of the single cart line representing a base item
 * (no customization, no set). Returns -1 when the product is not yet in cart.
 *
 * NOTE: Only valid for products where isCustomizableOrSet(product) === false.
 * Customizable products may have multiple cart lines and must use
 * getVirtualItemsForProduct instead.
 */
export const findBaseCartIndexForProduct = (
	product: ICatalogue,
	cart: ICatalogue[] | undefined | null
): number => {
	if (!cart) {
		return -1;
	}

	return cart.findIndex((line) => line.brand_menu_item_id === product.brand_menu_item_id);
};
